import { usePostHog } from "@posthog/react";
import { captureBuyerAgentGuideClicked } from "../../lib/marketplace-analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { HomeConnectionInput } from "../../hooks/useHomeMarketplaceData";
import { useHomeMarketplaceData } from "../../hooks/useHomeMarketplaceData";
import {
  useConsumerMarketplaceGate,
  type ConsumerMarketplaceGate,
} from "../../hooks/useConsumerMarketplaceGate";
import { useMyReviewForPro } from "../../hooks/useMyReviewForPro";
import {
  cancelConnection,
  disconnectMarketplaceConnection,
  withdrawConnectionRequest,
} from "../../lib/marketplace-connection-api";
import { submitReviewAnswer, type ReviewAnswer } from "../../lib/review-ask";
import { deriveHighestVerificationTier } from "../../lib/dashboard";
import {
  captureHomeAgentPreviewClicked,
  captureHomeHeroCtaClicked,
  captureHomeOfferTeaserClicked,
  captureHomeTierUnlockClicked,
  captureHomeViewed,
} from "../../lib/home-analytics";
import type { HomeHeroState } from "../../lib/home-hero-state";
import {
  continueWithMarketplaceChoice,
  stayWithCurrentAgent,
} from "../../lib/marketplace-recovery-api";
import { incentiveLabelFromOffer } from "../../lib/review-display";
import { TARGET_CONFIRM_HREF } from "../../lib/target-confirm";
import { joinWaitlist, type WaitlistPosition } from "../../lib/waitlist-api";
import {
  OverviewGreeting,
  type OverviewViewProps,
} from "../dashboard/dashboard-views";
import { AcceptConsentSheet } from "../marketplace/AcceptConsentSheet";
import type {
  DisconnectHandler,
  DisconnectParams,
} from "../marketplace/DisconnectMarketplaceDialog";
import { pickHomeBestOffer } from "../../lib/home-best-offer";
import { SectionLabel } from "../marketplace/OffersInbox";
import { RecoveryWindowCard } from "../marketplace/RecoveryWindowCard";
import { PlatformTourController } from "../overview/platform-tour-modal";
import { presentTourSteps } from "../overview/platform-tour";
import { ReferralShareSheet } from "../referral/referral-share-sheet";
import { useToast } from "../ui/toast";
import { AgentPreview } from "./AgentPreview";
import { ConnectedStack } from "./ConnectedStack";
import { HomeHero } from "./HomeHero";
import {
  computeHomeBlockVisibility,
  computeHomeSettledInputs,
} from "./HomeView.helpers";
import { NextMove } from "./NextMove";
import { OffersTeaser } from "./OffersTeaser";
import { PrepareRow } from "./PrepareRow";
import { RequestCard } from "./RequestCard";
import { TierUnlockRow } from "./TierUnlockRow";

export interface HomeViewProps extends OverviewViewProps {
  /** Needed by the marketplace reads this view owns (browse, inbox), the
   *  day-3 escape's cancel call, and the tier-unlock row's one extra
   *  request. */
  getToken: () => Promise<string | null>;
  /**
   * Plan 435 M2 (F1 review fix): the SINGLE `useMarketplaceConnection`
   * read, owned by the caller (`DashboardPage` already fetches it for the
   * sidebar/Passport pill) and threaded in here — mounting `HomeView` must
   * never issue a second `GET /api/marketplace/connection`.
   */
  marketplaceConnection: HomeConnectionInput;
  /** Owner-scoped geo-waitlist position, or null when not on any queue.
   *  Only chooses Join vs Share copy at `area_not_launched` — never a
   *  settle gate. */
  waitlistPosition?: WaitlistPosition | null;
  /** Re-fetches `waitlistPosition` (the caller's own `useWaitlist().refresh`,
   *  e.g. `DashboardPage`) — called after a successful `area_not_launched`
   *  "Join the waitlist" CTA so the hero re-derives from a real row (Share
   *  copy) on the next render, mirroring `BrowseWaitlistState`'s join→refresh
   *  idiom. */
  refreshWaitlist?: () => Promise<void>;
  /**
   * Preview/test-only seam (plan 435 M2 UI-fidelity fix item 3): forces the
   * gate tri-state instead of reading `useConsumerMarketplaceGate()`.
   *
   * `useConsumerMarketplaceGate`'s own `dev` short-circuit always resolves
   * "on" under `import.meta.env.DEV` (local dev, `/preview/*`, ui-verify) —
   * by design, so the real gate never has to race PostHog there. That means
   * the `off`/`loading` branches below are otherwise UNREACHABLE from any
   * DEV route, including `/preview/*`. This prop exists ONLY so
   * `/preview/home?state=gate-off|gate-loading` and `HomeView` tests can
   * render those branches for visual/content verification. It must never be
   * threaded from a real app route — `DashboardPage` never passes it.
   */
  forceGate?: ConsumerMarketplaceGate;
}

/**
 * Plan 435 M1–M3 (mfh-home-settle, mfh-home-route-gate-matrix,
 * mfh-hero-component, mfh-hero-analytics, mfh-offers-teaser,
 * mfh-agent-preview, mfh-tier-unlock-row, mfh-next-move, mfh-prepare-row).
 * Mounted by `DashboardPage` for the default `/dashboard` view.
 *
 * As of M3 this component no longer renders `OverviewView` at all (design
 * §4 block order: greeting → hero → blocks → next move → Prepare):
 *   - `loading` → `HomeHero variant="skeleton"` + Prepare, ZERO marketplace
 *                 reads, no next move.
 *   - `off`     → `HomeHero variant="readiness"` + a readiness-only
 *                 `NextMove` (never `publish`/`offers-expiry`) + Prepare,
 *                 ZERO marketplace reads, no marketplace copy or CTAs.
 *   - `on`      → settle-gated: skeleton until connection/browse/inbox
 *                 resolve or fail, then greeting + the marketplace hero +
 *                 the open-market body blocks (visibility per
 *                 `computeHomeBlockVisibility`) + Prepare.
 */
export function HomeView({
  getToken,
  marketplaceConnection,
  waitlistPosition = null,
  refreshWaitlist,
  forceGate,
  ...overviewProps
}: HomeViewProps) {
  // `useConsumerMarketplaceGate` is always called (React hook rules) even
  // when `forceGate` overrides its result — see the `forceGate` doc comment
  // on `HomeViewProps` for why the override seam exists.
  const resolvedGate = useConsumerMarketplaceGate();
  const gate = forceGate ?? resolvedGate;
  const marketplace = useHomeMarketplaceData(
    getToken,
    gate === "on",
    marketplaceConnection,
  );
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { pushToast } = useToast();
  const capture = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      posthog?.capture?.(event, properties);
    },
    [posthog],
  );

  const settled = gate === "on" && marketplace.settled;
  const settledInputs = settled
    ? computeHomeSettledInputs(
        marketplace,
        overviewProps.snapshot,
        waitlistPosition,
      )
    : null;

  // Plan 435 M4 (mfh-review-prompt). Called unconditionally (hook rules)
  // with the connected pro's id, or null for every other hero state — the
  // fetch is a no-op (never issued) in every case but `connected`.
  const connectedProUserId =
    settledInputs?.cardState.kind === "connected-payoff"
      ? settledInputs.cardState.proUserId ?? null
      : null;
  const myReview = useMyReviewForPro(getToken, connectedProUserId);

  // `home_viewed` fires exactly ONCE per mount, at the settled state —
  // never before the gate opens, and never again on a later background
  // refresh even if the settled hero kind itself changes (plan 435 M3
  // carried fix: the prior kind-keyed ref re-fired on a mid-session state
  // change; `home_viewed` is a page-view event, not a state-change event).
  const hasFiredViewedRef = useRef(false);
  useEffect(() => {
    if (!settledInputs || hasFiredViewedRef.current) return;
    hasFiredViewedRef.current = true;
    captureHomeViewed(
      capture,
      settledInputs.heroState.kind,
      settledInputs.agentCount,
      settledInputs.offersCount,
    );
  }, [capture, settledInputs]);

  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  // Plan 435 M4 (mfh-recovery-card). Mirrors `OffersPage`'s exact recovery
  // wiring (busy flag + the "Continue with my choice" consent sheet) —
  // never a forked implementation.
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryConsentOpen, setRecoveryConsentOpen] = useState(false);

  if (gate === "loading") {
    return (
      <div data-home-gate={gate} data-testid="home-view">
        <OverviewGreeting firstName={overviewProps.snapshot.firstName} />
        <div className="mb-4">
          <HomeHero snapshot={overviewProps.snapshot} variant="skeleton" />
        </div>
        <PrepareRow
          items={overviewProps.snapshot.financeReadinessPreview ?? []}
          onOpenFinancials={overviewProps.onOpenFinancials}
          onOpenRateWatch={overviewProps.onOpenRateWatch}
          onSelectVerificationSource={overviewProps.onSelectVerificationSource}
          snapshot={overviewProps.snapshot}
        />
      </div>
    );
  }

  if (gate === "on" && !settledInputs) {
    return (
      <div data-home-gate={gate} data-testid="home-view">
        <OverviewGreeting firstName={overviewProps.snapshot.firstName} />
        <HomeHero snapshot={overviewProps.snapshot} variant="skeleton" />
      </div>
    );
  }

  if (gate === "off") {
    return (
      <div data-home-gate={gate} data-testid="home-view">
        <OverviewGreeting firstName={overviewProps.snapshot.firstName} />
        <div className="mb-4">
          <HomeHero
            onVerify={overviewProps.onVerificationUpgrade}
            snapshot={overviewProps.snapshot}
            variant="readiness"
          />
        </div>
        <div className="mb-4">
          <NextMove
            isLive={false}
            onCompleteOnboarding={overviewProps.onCompleteOnboarding}
            onSelectVerificationSource={overviewProps.onSelectVerificationSource}
            profileCompleteness={overviewProps.snapshot.profileCompleteness}
            profileCompletenessMissing={
              overviewProps.snapshot.profileCompletenessMissing
            }
            readinessOnly
            topOffer={null}
            verificationTier={deriveHighestVerificationTier(
              overviewProps.snapshot,
            )}
          />
        </div>
        <PrepareRow
          items={overviewProps.snapshot.financeReadinessPreview ?? []}
          onOpenFinancials={overviewProps.onOpenFinancials}
          onOpenRateWatch={overviewProps.onOpenRateWatch}
          onSelectVerificationSource={overviewProps.onSelectVerificationSource}
          snapshot={overviewProps.snapshot}
        />
      </div>
    );
  }

  // gate === "on" && settledInputs is defined past this point.
  const {
    heroState,
    cardState,
    area,
    browse,
    inbox,
    currentTier,
    verificationTier,
    firstAgentProUserId,
    topOffer,
  } = settledInputs!;

  const recoveryWindow = marketplace.recoveryWindow;
  const visibility = computeHomeBlockVisibility(
    heroState.kind,
    Boolean(recoveryWindow),
  );

  // The top made-for-you offer, picked once: the teaser shows it, and the
  // agent tiles skip its agent so each agent appears on Home once.
  const teaserOffer = visibility.showOffers
    ? (inbox?.made_for_you[0] ?? null)
    : null;
  const previewAgents = (browse?.agents ?? []).filter(
    (agent) => agent.identity.pro_user_id !== teaserOffer?.pro_user_id,
  );

  // Code-review fix (M3 iteration 2, finding 5; re-fix F5): mirrors the
  // exact presence conditions `OffersTeaser`/`AgentPreview` render under —
  // AND'd with `visibility.showOffers`/`showAgents` so a block the current
  // hero state HIDES (e.g. a residual inbox under `waiting_agent`/
  // `connected`) is never a tour step either. Presence alone isn't
  // sufficient: the hero kind can hide a block whose underlying data is
  // still non-empty.
  const offersPresent = teaserOffer !== null;
  const agentsPresent =
    visibility.showAgents &&
    (heroState.kind === "area_not_launched" || previewAgents.length > 0);
  const tourSteps = presentTourSteps({ offersPresent, agentsPresent });

  // area_not_launched's primary CTA is "Join the waitlist" only when the
  // buyer has no row yet (see `deriveHomeHeroState` case 6); the other
  // branch ("Share your link") and `live_quiet`'s "Share passport"
  // secondary both open the shared waitlist/referral share sheet rather
  // than navigating (plan 435 M3 carried fix — M2 left both as a static
  // Passport link).
  const isJoinWaitlistCta =
    heroState.kind === "area_not_launched" &&
    heroState.primary?.label === "Join the waitlist";
  const isShareLinkCta =
    heroState.kind === "area_not_launched" &&
    heroState.primary?.label === "Share your link";
  const isSharePassportCta =
    heroState.kind === "live_quiet" && heroState.secondary?.label === "Share passport";

  const handleJoinWaitlist = async () => {
    try {
      await joinWaitlist(getToken);
      await refreshWaitlist?.();
      pushToast({
        title: area
          ? `You're on the waitlist for ${area}.`
          : "You're on the waitlist.",
      });
    } catch {
      pushToast({ title: "Couldn't join the waitlist — try again." });
    }
  };

  const handlePrimary = () => {
    captureHomeHeroCtaClicked(capture, heroState.kind, "primary");
    if (isJoinWaitlistCta) {
      void handleJoinWaitlist();
      return;
    }
    if (isShareLinkCta) {
      setShareSheetOpen(true);
      return;
    }
    navigateForCta(navigate, heroState, "primary");
  };
  const handleBestOffer = (href: string) => {
    captureHomeHeroCtaClicked(capture, heroState.kind, "best_offer");
    navigate(href);
  };
  const handleSecondary = () => {
    captureHomeHeroCtaClicked(capture, heroState.kind, "secondary");
    if (isSharePassportCta) {
      setShareSheetOpen(true);
      return;
    }
    navigateForCta(navigate, heroState, "secondary");
  };
  const handleChipClick = () => {
    captureHomeHeroCtaClicked(capture, heroState.kind, "chip");
    // 2026-10-08: the readiness chip opens the Readiness page — a score chip
    // that opened a chat drawer surprised people. Ask Sill keeps its header
    // pill, mobile tab and ⌘K entries (M6 asd-entry-repoint superseded here).
    navigate("/dashboard?view=readiness");
  };
  const handleCancelConnection = async (connectionId: string) => {
    await cancelConnection(getToken, connectionId);
    await marketplace.refreshConnection();
  };

  // Plan 435 M4 (mfh-request-card). On success the caller's pendingRequest
  // clears via `refreshConnection()`, so `waiting_agent` resolves away and
  // the card unmounts along with it — the hero re-derives from the SAME
  // settle-gated read everything else on this page already uses. A
  // rejection propagates to `RequestCard`'s own inline error state; this
  // handler never swallows it.
  const handleWithdrawRequest = async (requestId: string) => {
    await withdrawConnectionRequest(getToken, requestId);
    await marketplace.refreshConnection();
  };

  const handleDisconnect = async (params: DisconnectParams) => {
    await disconnectMarketplaceConnection(
      getToken,
      params.connectionId,
      params.reason,
      params.note,
    );
    await marketplace.refreshConnection();
  };

  // Plan 462 M5: the no-contact banner's disconnect. Unlike `handleDisconnect`
  // it does NOT refresh here: ending the connection would unmount
  // `ConnectedStack` (and the dialog's done state) before the buyer reads the
  // outcome, so the refresh waits for the dialog to close (`onEnded`).
  const handleNoContactExitDisconnect: DisconnectHandler = (params) =>
    disconnectMarketplaceConnection(
      getToken,
      params.connectionId,
      params.reason,
      params.note,
      params.republish,
    );

  const handleSubmitReview = async (answer: ReviewAnswer) => {
    if (!connectedProUserId || !myReview.state) return;
    try {
      await submitReviewAnswer(getToken, connectedProUserId, myReview.state, answer);
    } finally {
      // Re-read on a refusal too: a 409 means the server's state moved.
      await myReview.refetch();
    }
  };

  const isLive = overviewProps.snapshot.portalDiscoverable === true;
  const bestOffer = pickHomeBestOffer(inbox);
  const amber = Boolean(recoveryWindow);
  // The incumbent's display name for the recovery card's "Stay with
  // {incumbent}" copy — reuses the SAME `browse.incumbent` Home already
  // fetched for the agent preview, never a second request.
  const incumbentName = browse?.incumbent?.identity.display_name;

  return (
    <div
      data-home-card-state={cardState.kind}
      data-home-gate={gate}
      data-testid="home-view"
    >
      <OverviewGreeting firstName={overviewProps.snapshot.firstName} />
      <div className="mb-4">
        <PlatformTourController
          bootstrapTourState={overviewProps.bootstrapTourState}
          ready
          steps={tourSteps}
        />
      </div>
      <div className="mb-8">
        <HomeHero
          amber={amber}
          onCancelConnection={handleCancelConnection}
          onChipClick={handleChipClick}
          onPrimary={handlePrimary}
          onSecondary={handleSecondary}
          onGuideClick={() => captureBuyerAgentGuideClicked(capture, "home_hero")}
          bestOffer={bestOffer}
          onBestOffer={handleBestOffer}
          snapshot={overviewProps.snapshot}
          state={heroState}
          variant="marketplace"
        />
      </div>

      {recoveryWindow ? (
        <div className="mb-8 space-y-3" data-testid="home-recovery">
          {/* Review fixes 5/6: the Home block label must be distinct from
              RecoveryWindowCard's own "Response window" eyebrow (never a
              literal duplicate), and every Home block heading uses the
              DEFAULT (non-quiet) SectionLabel, matching the M3 blocks. */}
          <SectionLabel label="Your decision" />
          <RecoveryWindowCard
            busy={recoveryBusy}
            continueAvailable
            incumbentName={incumbentName}
            onContinue={() => setRecoveryConsentOpen(true)}
            onStay={() => {
              setRecoveryBusy(true);
              void stayWithCurrentAgent(getToken, recoveryWindow.id)
                .then(() => marketplace.refreshRecoveryWindow())
                .finally(() => setRecoveryBusy(false));
            }}
            window={recoveryWindow}
          />
          {recoveryWindow.target_kind === "active_offer" ? (
            <AcceptConsentSheet
              agentName={recoveryWindow.target_pro_display_name ?? "your marketplace choice"}
              marketplaceRecoverySeverance
              onAccept={async () => {
                await continueWithMarketplaceChoice(getToken, recoveryWindow.id);
                await marketplace.refreshRecoveryWindow();
                await marketplace.refreshConnection();
                return { connection: {} as never };
              }}
              onClose={() => setRecoveryConsentOpen(false)}
              open={recoveryConsentOpen}
              variant="active"
            />
          ) : null}
          {recoveryWindow.target_kind === "standing" ? (
            <AcceptConsentSheet
              agentName={recoveryWindow.target_pro_display_name ?? "your marketplace choice"}
              marketplaceRecoverySeverance
              onAccept={async () => {
                await continueWithMarketplaceChoice(getToken, recoveryWindow.id);
                await marketplace.refreshRecoveryWindow();
                await marketplace.refreshConnection();
                return { request: { id: recoveryWindow.id } as never };
              }}
              onClose={() => setRecoveryConsentOpen(false)}
              open={recoveryConsentOpen}
              variant="standing"
            />
          ) : null}
        </div>
      ) : null}

      {visibility.showRequestCard && settledInputs!.pendingRequest ? (
        <div className="mb-4">
          <RequestCard
            onWithdraw={handleWithdrawRequest}
            request={settledInputs!.pendingRequest}
          />
        </div>
      ) : null}

      {visibility.showConnectedStack && cardState.kind === "connected-payoff" ? (
        <div className="mb-4">
          <ConnectedStack
            checkin={settledInputs!.connection?.checkin}
            connection={cardState}
            getToken={getToken}
            incentiveLabel={incentiveLabelFromOffer(cardState.acceptedOffer)}
            reviewState={myReview.state}
            onAddProperty={overviewProps.onAddProperty}
            onCheckinAnswered={() => {
              void marketplace.refreshConnection();
              // A closed answer can carry a review (the close-upgrade modal);
              // re-read it so the standalone ReviewPrompt never re-asks.
              void myReview.refetch();
            }}
            onDisconnect={handleDisconnect}
            noContactExit={settledInputs!.connection?.no_contact_exit}
            onNoContactExitDisconnect={handleNoContactExitDisconnect}
            onNoContactExitEnded={() => void marketplace.refreshConnection()}
            onSubmitReview={handleSubmitReview}
            role="agent"
            trackedProperties={overviewProps.snapshot.trackedProperties}
          />
        </div>
      ) : null}

      <div className="space-y-8">
        {visibility.showOffers ? (
          <OffersTeaser
            inboxTotal={
              (inbox?.made_for_you.length ?? 0) +
              (inbox?.available_to_you.length ?? 0)
            }
            offer={teaserOffer}
            onOfferTeaserClicked={() => captureHomeOfferTeaserClicked(capture)}
          />
        ) : null}

        {visibility.showAgents ? (
          <AgentPreview
            agents={previewAgents}
            areaLabel={area}
            areaNotLaunched={heroState.kind === "area_not_launched"}
            launchedAreas={marketplace.browse?.launched_areas}
            onAgentClick={(position) =>
              captureHomeAgentPreviewClicked(capture, position, heroState.kind)
            }
            sectionState={
              heroState.kind === "waiting_agent"
                ? "waiting"
                : isLive
                  ? "live"
                  : "unpublished"
            }
            tier={currentTier}
          />
        ) : null}

        {visibility.showUnlock ? (
          <TierUnlockRow
            currentTier={currentTier}
            firstAgentProUserId={firstAgentProUserId}
            getToken={getToken}
            onUnlockClicked={(current, locked) =>
              captureHomeTierUnlockClicked(capture, current, locked)
            }
          />
        ) : null}

        {visibility.showNextMove ? (
          <NextMove
            isLive={isLive}
            onCompleteOnboarding={overviewProps.onCompleteOnboarding}
            onSelectVerificationSource={overviewProps.onSelectVerificationSource}
            profileCompleteness={overviewProps.snapshot.profileCompleteness}
            profileCompletenessMissing={
              overviewProps.snapshot.profileCompletenessMissing
            }
            readinessOnly={false}
            topOffer={
              topOffer
                ? {
                    proDisplayName: topOffer.pro_display_name,
                    expiresAt: topOffer.expires_at,
                  }
                : null
            }
            verificationTier={verificationTier}
          />
        ) : null}
      </div>

      <div className="mt-8">
        <PrepareRow
          items={overviewProps.snapshot.financeReadinessPreview ?? []}
          onOpenFinancials={overviewProps.onOpenFinancials}
          onOpenRateWatch={overviewProps.onOpenRateWatch}
          onSelectVerificationSource={overviewProps.onSelectVerificationSource}
          snapshot={overviewProps.snapshot}
        />
      </div>

      <ReferralShareSheet
        onOpenChange={setShareSheetOpen}
        open={shareSheetOpen}
        waitlistArea={heroState.kind === "area_not_launched" ? area : null}
      />
    </div>
  );
}

function navigateForCta(
  navigate: ReturnType<typeof useNavigate>,
  heroState: HomeHeroState,
  cta: "primary" | "secondary",
) {
  const href = ctaHref(heroState, cta);
  if (href) navigate(href);
}

// The in-passport publish control's anchor id — the SAME `?view=passport`
// + hash idiom `getMarketplacePublishResumePath` (lib/onboarding.ts) and
// `DisconnectMarketplaceDialog` already use to land on
// `PassportPublishBlock`. Reused verbatim rather than inventing a second
// "go live" destination.
const PUBLISH_BLOCK_HREF = "/dashboard?view=passport#passport-publish-action";

/**
 * Static CTA destinations — every one of these routes already exists and
 * is reachable from elsewhere in the app (Passport, Browse, Offers,
 * Threads, Team). Two exceptions already wired above (plan 435 M2/M3
 * fixes): `connected`'s primary reads `heroState.proUserId` for the Your
 * Team Message deep-link shape, and `area_not_launched`/`live_quiet`'s
 * share CTAs are handled by the caller (`handlePrimary`/`handleSecondary`)
 * before this function is ever reached.
 */
function ctaHref(
  heroState: HomeHeroState,
  cta: "primary" | "secondary",
): string | null {
  const { kind } = heroState;
  switch (kind) {
    case "connected":
      return cta === "primary"
        ? heroState.proUserId
          ? `/threads?pro=${heroState.proUserId}`
          : "/threads"
        : "/team";
    case "finalizing":
    case "finalizing_escape":
      return null;
    case "waiting_agent":
      return cta === "primary" ? "/offers" : "/marketplace/browse?role=agent";
    case "no_target":
      // "Set target area" / "Confirm target" → the target-area row itself,
      // not the page top. The same destination the Browse and /offers
      // confirm line uses (plan 454 M5).
      return TARGET_CONFIRM_HREF;
    case "area_not_launched":
      // Primary (Join / Share) is handled by the caller; the secondary
      // "Change target area" lands on the target-area row itself — the ONE
      // target-area anchor (`TARGET_CONFIRM_HREF`) that `no_target`, the
      // confirm line, and `BrowseWaitlistState`'s block all use.
      return cta === "primary" ? "/dashboard?view=passport" : TARGET_CONFIRM_HREF;
    case "live_offers":
      return cta === "primary" ? "/offers" : "/marketplace/browse?role=agent";
    case "live_quiet":
      return cta === "primary" ? "/marketplace/browse?role=agent" : "/dashboard?view=passport";
    case "unpublished_agents":
    case "unpublished_none":
      return cta === "primary" ? PUBLISH_BLOCK_HREF : "/marketplace/browse?role=agent";
  }
}
